import { useEffect, useState } from 'react';

/**
 * useIsMounted hook to check if the component is mounted.
 *
 * This hook is particularly useful for avoiding hydration mismatches between server-rendered
 * and client-rendered content in the applications. It ensures that side effects or UI
 * updates that depend on client-specific information (e.g., browser APIs) are only applied
 * after the component has been mounted on the client.
 *
 * By returning `false` initially, it signals that the component hasn't fully mounted,
 * thereby preventing any rendering that relies on client-side resources from happening
 * during the initial server-side render. Once mounted, it returns `true`, enabling
 * client-side-specific logic to run. This effectively delays operations that might differ
 * between the server and the client, avoiding hydration errors.
 *
 * @returns {boolean}
 * Returns `false` initially, then `true` after the first render.
 */
export const useIsMounted = () => {
  const [isMounted, setIsMounted] = useState( false );

  useEffect( () => {
    setIsMounted( true );
  }, [] );

  return isMounted;
};
